import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, SkipForward, ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

/**
 * MINDFUL COOLDOWN BLOCK (Cierre Consciente)
 *
 * Bloque final de 4-6 minutos al terminar la sesión.
 * 3-4 ejercicios encadenados (respiración, movilidad suave, equilibrio, cierre).
 *
 * Props:
 *   block: { id, name, subtitle, total_minutes, exercises: [{ id, name, duration_seconds, instructions }] }
 *   onFinish(): callback al terminar el bloque completo
 *   onSkipAll(): callback al saltar todo el bloque
 */
const MindfulCooldownBlock = ({ block, onFinish, onSkipAll }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const exercises = block?.exercises || [];
  const current = exercises[currentIdx];
  const duration = current?.duration_seconds || 60;
  const [timeLeft, setTimeLeft] = useState(duration);
  const intervalRef = useRef(null);

  // Reset timer cuando cambia el ejercicio
  useEffect(() => {
    setTimeLeft(duration);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          // Auto-avanzar al siguiente
          setTimeout(() => goNext(), 400);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  const goNext = () => {
    if (currentIdx < exercises.length - 1) {
      setCurrentIdx((prev) => prev + 1);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      onFinish?.();
    }
  };

  const overallProgress = ((currentIdx + (1 - timeLeft / duration)) / exercises.length) * 100;

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-gradient-to-br from-amber-950 via-emerald-950 to-zinc-950 flex flex-col p-6 animate-fade-in overflow-y-auto"
      data-testid="mindful-cooldown-overlay"
    >
      {/* Header */}
      <div className="w-full max-w-md mx-auto pt-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 border border-amber-500/40 rounded-full mb-3">
          <Sparkles className="h-4 w-4 text-amber-300" />
          <span className="text-sm font-medium text-amber-200 tracking-wide uppercase">
            {block?.name || 'Cierre Consciente'}
          </span>
        </div>
        {block?.subtitle && (
          <p className="text-xs text-amber-200/70 mb-3">{block.subtitle}</p>
        )}
        <Progress value={overallProgress} className="h-1.5 bg-white/10" />
        <div className="flex items-center justify-between mt-2 text-xs text-white/60">
          <span>
            Paso {currentIdx + 1} de {exercises.length}
          </span>
          <span>~ {block?.total_minutes || 5} min total</span>
        </div>
      </div>

      {/* Ejercicio actual */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md mx-auto">
        <div className="text-7xl font-bold text-white tabular-nums mb-6" data-testid="cooldown-timer">
          {timeLeft}s
        </div>
        <h2 className="text-2xl font-bold text-white text-center mb-3 px-4" data-testid="cooldown-exercise-name">
          {current.name}
        </h2>
        <p className="text-base text-white/80 text-center leading-relaxed px-4">
          {current.instructions}
        </p>
      </div>

      {/* Footer */}
      <div className="w-full max-w-md mx-auto grid grid-cols-2 gap-3 pb-6">
        <Button
          variant="outline"
          onClick={onSkipAll}
          className="h-14 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          data-testid="cooldown-skip-all-btn"
        >
          <SkipForward className="h-5 w-5 mr-2" />
          Saltar bloque
        </Button>
        <Button
          onClick={goNext}
          className="h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          data-testid="cooldown-next-btn"
        >
          {currentIdx < exercises.length - 1 ? (
            <>
              Siguiente
              <ArrowRight className="h-5 w-5 ml-2" />
            </>
          ) : (
            <>
              <Check className="h-5 w-5 mr-2" />
              Terminar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default MindfulCooldownBlock;
